import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Select, InputNumber, Spin, Empty, Input } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiCortesInstance, authHeaderCortes } from "../../redux/actions/cortes/cortes";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Apertura", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function CorteDetallePage() {
  const { idCorte } = useParams();
  const navigate = useNavigate();

  const [corte, setCorte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");

  const [motivos, setMotivos] = useState([]);
  const [addRetiroOpen, setAddRetiroOpen] = useState(false);
  const [savingRetiro, setSavingRetiro] = useState(false);
  const [nuevoRetiro, setNuevoRetiro] = useState({});

  const [cerrarOpen, setCerrarOpen] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [cierre, setCierre] = useState({ efectivo_real: null, tarjeta_real: null, transferencia_real: null });

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchCorte = useCallback(() => {
    setLoading(true);
    apiCortesInstance.get(`/cortes/${idCorte}`, { headers: authHeaderCortes() })
      .then((res) => setCorte(res.data))
      .catch(() => toast.error("No se pudo cargar el corte"))
      .finally(() => setLoading(false));
  }, [idCorte]);

  useEffect(() => { fetchCorte(); }, [fetchCorte]);

  useEffect(() => {
    apiCortesInstance.get("/cortes/catalogos/motivos-retiro", { headers: authHeaderCortes() })
      .then((r) => setMotivos((r.data?.items ?? []).map((m) => ({ label: m.nombre, value: m.id_motivo_retiro }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiCortesInstance.get(`/cortes/${idCorte}/actividad`, { headers: authHeaderCortes() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idCorte]);

  const handleAddRetiro = async () => {
    if (!nuevoRetiro.id_motivo_retiro || nuevoRetiro.monto == null) {
      toast.warning("Selecciona el motivo e ingresa el monto");
      return;
    }
    setSavingRetiro(true);
    try {
      await apiCortesInstance.post(`/cortes/${idCorte}/retiros`, nuevoRetiro, { headers: authHeaderCortes() });
      toast.success("Retiro registrado");
      setAddRetiroOpen(false);
      setNuevoRetiro({});
      fetchCorte();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el retiro");
    } finally {
      setSavingRetiro(false);
    }
  };

  const handleCerrar = async () => {
    if (cierre.efectivo_real == null || cierre.tarjeta_real == null || cierre.transferencia_real == null) {
      toast.warning("Captura los tres montos contados");
      return;
    }
    setCerrando(true);
    try {
      await apiCortesInstance.post(`/cortes/${idCorte}/cerrar`, cierre, { headers: authHeaderCortes() });
      toast.success("Corte cerrado");
      setCerrarOpen(false);
      fetchCorte();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cerrar el corte");
    } finally {
      setCerrando(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!corte) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Corte no encontrado" />
      </div>
    );
  }

  const totalEsperado = parseFloat(corte.efectivo_esperado || 0) + parseFloat(corte.tarjeta_esperado || 0) + parseFloat(corte.transferencia_esperado || 0);
  const totalReal = corte.cerrado ? parseFloat(corte.efectivo_real || 0) + parseFloat(corte.tarjeta_real || 0) + parseFloat(corte.transferencia_real || 0) : null;
  const diferencia = corte.cerrado ? totalReal - totalEsperado : null;

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">CORTE #{corte.id_corte}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> {corte.fecha}</span>
                <span className="cd-meta-item"><i className="fa-solid fa-user" /> {corte.cajero_nombre}</span>
              </div>
            </div>
            <div className="cd-header-actions">
              {!corte.cerrado && (
                <Button type="primary" icon={<i className="fa-solid fa-lock" />} onClick={() => setCerrarOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Cerrar corte
                </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "ventas" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("ventas")}>
              <i className="fa-solid fa-receipt" /> Ventas del dia ({(corte.ventas || []).length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "retiros" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("retiros")}>
              <i className="fa-solid fa-money-bill-transfer" /> Retiros ({(corte.retiros || []).length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-vault"></i></div>
                <h2 className="cd-card-title">Desglose esperado vs real</h2>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", fontSize: 11, textTransform: "uppercase", color: "#6b7280" }}>
                      <th style={{ padding: 8 }}>Concepto</th>
                      <th style={{ padding: 8 }}>Esperado</th>
                      <th style={{ padding: 8 }}>Real</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: 14 }}>
                    <tr style={{ borderTop: "1px solid #f0edf2" }}>
                      <td style={{ padding: 8 }}>Fondo inicial</td>
                      <td style={{ padding: 8 }} colSpan={2}>{money(corte.fondo_inicial)}</td>
                    </tr>
                    <tr style={{ borderTop: "1px solid #f0edf2" }}>
                      <td style={{ padding: 8 }}>Efectivo</td>
                      <td style={{ padding: 8 }}>{money(corte.efectivo_esperado)}</td>
                      <td style={{ padding: 8 }}>{corte.cerrado ? money(corte.efectivo_real) : "—"}</td>
                    </tr>
                    <tr style={{ borderTop: "1px solid #f0edf2" }}>
                      <td style={{ padding: 8 }}>Tarjeta</td>
                      <td style={{ padding: 8 }}>{money(corte.tarjeta_esperado)}</td>
                      <td style={{ padding: 8 }}>{corte.cerrado ? money(corte.tarjeta_real) : "—"}</td>
                    </tr>
                    <tr style={{ borderTop: "1px solid #f0edf2" }}>
                      <td style={{ padding: 8 }}>Transferencia</td>
                      <td style={{ padding: 8 }}>{money(corte.transferencia_esperado)}</td>
                      <td style={{ padding: 8 }}>{corte.cerrado ? money(corte.transferencia_real) : "—"}</td>
                    </tr>
                    <tr style={{ borderTop: "2px solid #e2e8f0", fontWeight: 700 }}>
                      <td style={{ padding: 8 }}>Total</td>
                      <td style={{ padding: 8 }}>{money(totalEsperado)}</td>
                      <td style={{ padding: 8 }}>{corte.cerrado ? money(totalReal) : "—"}</td>
                    </tr>
                    {corte.cerrado === 1 && (
                      <tr>
                        <td style={{ padding: 8, fontWeight: 700 }}>Diferencia</td>
                        <td colSpan={2} style={{ padding: 8, fontWeight: 800, color: Math.abs(diferencia) > 0.01 ? "#b91c1c" : "#15803d" }}>
                          {money(diferencia)} {Math.abs(diferencia) > 0.01 ? (diferencia < 0 ? "(faltante)" : "(sobrante)") : "(cuadrado)"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-info"></i></div>
                <h2 className="cd-card-title">Datos generales</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><span className="cd-field-label">Estado</span><span className="cd-field-value">{corte.cerrado ? "Cerrado" : "Abierto"}</span></div>
                <div><span className="cd-field-label">Abrio</span><span className="cd-field-value">{fmtFechaCorta(corte.datetime)}</span></div>
                {corte.cerrado === 1 && <div><span className="cd-field-label">Cerro</span><span className="cd-field-value">{corte.cerro_nombre}</span></div>}
              </div>
            </div>
          </div>
        )}

        {activeTab === "ventas" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-receipt"></i></div>
              <h2 className="cd-card-title">Ventas del dia</h2>
            </div>
            {(corte.ventas || []).length === 0 ? (
              <Empty description="No hubo ventas este dia" />
            ) : (
              <div className="cd-pagos-list">
                {corte.ventas.map((v) => (
                  <div
                    key={v.id_venta}
                    style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2", cursor: "pointer" }}
                    onClick={() => navigate(`/app/ventas/${v.id_venta}`)}
                  >
                    <div><span className="cd-field-label">Venta</span><div className="cd-field-value">#{v.id_venta}</div></div>
                    <div><span className="cd-field-label">Cliente</span><div className="cd-field-value">{v.cliente_nombre || "Publico general"}</div></div>
                    <div><span className="cd-field-label">Total</span><div className="cd-field-value">{money(v.total)}</div></div>
                    <div><span className="cd-field-label">Estado</span><div className="cd-field-value" style={{ color: v.cancelada ? "#b91c1c" : "#15803d" }}>{v.cancelada ? "Cancelada" : "Valida"}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "retiros" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-money-bill-transfer"></i></div>
                <h2 className="cd-card-title">Retiros de caja</h2>
              </div>
              {!corte.cerrado && (
                <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setAddRetiroOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Agregar retiro
                </Button>
              )}
            </div>
            {(corte.retiros || []).length === 0 ? (
              <Empty description="Todavia no hay retiros registrados" />
            ) : (
              <div className="cd-pagos-list">
                {corte.retiros.map((r) => (
                  <div key={r.id_retiro} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Motivo</span><div className="cd-field-value">{r.motivo_nombre}</div></div>
                    <div><span className="cd-field-label">Proveedor</span><div className="cd-field-value">{r.proveedor_nombre || "—"}</div></div>
                    <div><span className="cd-field-label">Monto</span><div className="cd-field-value">{money(r.monto)}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(r.datetime)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={addRetiroOpen}
        onCancel={() => setAddRetiroOpen(false)}
        onOk={handleAddRetiro}
        okText="Registrar retiro"
        cancelText="Cancelar"
        title="Registrar retiro de caja"
        confirmLoading={savingRetiro}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Select placeholder="Motivo" options={motivos} value={nuevoRetiro.id_motivo_retiro} onChange={(v) => setNuevoRetiro((f) => ({ ...f, id_motivo_retiro: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Monto" min={0} precision={2} value={nuevoRetiro.monto} onChange={(v) => setNuevoRetiro((f) => ({ ...f, monto: v }))} />
          <Input.TextArea rows={2} placeholder="Nota (opcional)" value={nuevoRetiro.nota || ""} onChange={(e) => setNuevoRetiro((f) => ({ ...f, nota: e.target.value }))} />
        </div>
      </Modal>

      <Modal
        open={cerrarOpen}
        onCancel={() => setCerrarOpen(false)}
        onOk={handleCerrar}
        okText="Cerrar corte"
        cancelText="Cancelar"
        title="Cerrar corte de caja"
        confirmLoading={cerrando}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" }, danger: false }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>Captura lo contado fisicamente:</div>
          <InputNumber style={{ width: "100%" }} placeholder="Efectivo contado" min={0} precision={2} value={cierre.efectivo_real} onChange={(v) => setCierre((f) => ({ ...f, efectivo_real: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Tarjeta contado" min={0} precision={2} value={cierre.tarjeta_real} onChange={(v) => setCierre((f) => ({ ...f, tarjeta_real: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Transferencia contado" min={0} precision={2} value={cierre.transferencia_real} onChange={(v) => setCierre((f) => ({ ...f, transferencia_real: v }))} />
        </div>
      </Modal>
    </div>
  );
}
